import { inject, Injectable, isDevMode } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { Environment } from '@rma/generic/util/environment';
import { sha256 } from 'js-sha256';
import {
  MetaEventCustomParams,
  MetaEventOptions,
  MetaEventParams,
  MetaPixelStandardEvents,
  MetaUserData,
  RmaCustomEventParams,
} from '../domain/fb-events.model';
import { MetaPixelContextService } from '../util-meta-pixel-context/meta-pixel-context.service';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

type MetaPixelTrackType = 'track' | 'trackCustom';

@Injectable({
  providedIn: 'root',
})
export class MetaPixelService {
  private readonly window = inject(WA_WINDOW);
  private readonly context = inject(MetaPixelContextService);
  private readonly environment = inject(Environment);

  /**
   * Log the events to the console, so you can debug the events. If this is true, a test will fail
   */
  private readonly is_debug = false;

  /** UserId that has been initialized with the meta pixel (if any) */
  private initUserId?: string;

  /** User data for anonymous users */
  private anonymousUser?: MetaUserData;

  /**
   * Tracks a standard meta pixel event.
   * DO NOT include any PII (personally identifiable information). E.g. do not include email, name, phone number, etc. Logged-in user info is automatically added. Can use identifyUser() to identify details of an anonymous user.
   * @param event The standard event name
   * @param data Standard event data. These are the preferred/expected parameters from meta for each standard event.
   * @param options Event options (e.g. eventID for deduplication)
   * @param customData Custom event data to add. These are in addition to the standard parameters. These can be any standard defined meta properties used by other events, or any custom value we want to track
   */
  public track<E extends MetaPixelStandardEvents>(
    event: E,
    data?: MetaEventParams<E>,
    options?: MetaEventOptions,
    customData?: MetaEventCustomParams<E>,
  ) {
    this.trackEvent('track', event, data, options, customData);
  }

  /**
   * Tracks a custom meta pixel event (ie any event that doesn't fit one of the standard meta defined event names)
   * DO NOT include any PII (personally identifiable information). E.g. do not include email, name, phone number, etc. Logged-in user info is automatically added. Can use identifyUser() to identify details of an anonymous user.
   * @param event The custom event name
   * @param data Custom event data to add. These can be any standard defined meta properties used by other events, or any custom value we want to track
   * @param options Event options (e.g. eventID for deduplication)
   */
  public trackCustom(event: string, data?: RmaCustomEventParams & { [key: string]: unknown }, options?: MetaEventOptions) {
    this.trackEvent('trackCustom', event, data, options);
  }

  /**
   * Provides information about an anonymous (non-logged in) user for meta pixel advanced matching.
   * Only used if a user is not supplied by the MetaPixelContextService (will be ignored otherwise).
   * Useful when a user self identifies via a contact form.
   */
  public identifyUser(user: MetaUserData): void {
    const pixelContext = this.context.getContext();

    if (pixelContext.isImpersonating || pixelContext.user) {
      // Ignore the user details if the user is logged in
      return;
    }

    if (this.anonymousUser === user) {
      // User is already identified
      return;
    }

    this.anonymousUser = user;
    this.initUser(user);
  }

  private trackEvent(
    type: MetaPixelTrackType,
    event: string,
    data: Record<string, unknown> = {},
    options?: MetaEventOptions,
    customData?: Record<string, unknown>,
  ): void {
    const params = { ...data, ...customData };

    if (this.is_debug && isDevMode()) {
      this.logEventToConsole(type, event, params, options);
      return;
    }

    const pixelContext = this.context.getContext();

    if (pixelContext.isImpersonating) {
      // Do not track events for RAdmin users
      return;
    }

    // Ensure we have initialized meta pixel for the current user
    this.initUser(pixelContext.user);

    if (this.window?.fbq) {
      this.window.fbq(type, event, params, options);
    } else {
      console.error(
        `[MetaPixel] not logged: type: [${type}] event: [${event}] params: [${JSON.stringify(params)}] options: [${JSON.stringify(options ?? {})}]`,
      );
    }
  }

  /** (Re)initializes the Meta Pixel with the user's data for advanced matching */
  private initUser(user?: MetaUserData): void {
    const pixelId = this.environment.tracking.facebookPixelId;

    if (!user) {
      // No user to initialize
      return;
    }

    if (this.initUserId === user.external_id && user !== this.anonymousUser) {
      // User already initialized
      return;
    }

    this.initUserId = user.external_id;

    // We will pre-hash the user data before sending to the meta pixel
    // Meta would hash it if we did not, but this is best practice.
    // It also helps keep PII out of logs
    const userData = this.normalizeAndHash(user);

    if (this.is_debug && isDevMode()) {
      console.log(`[MetaPixel] init pixel with id: ${pixelId} and user data: `, userData);
    } else if (this.window?.fbq) {
      this.window.fbq('init', pixelId, userData);
    } else {
      console.error(`[MetaPixel] not initialized: pixelId: ${pixelId} user data: ${JSON.stringify(userData)}`);
    }
  }

  /** Normalizes each user property and hashes sensitive information. Will not re-hash already hashed values. */
  private normalizeAndHash(user?: MetaUserData): MetaUserData {
    if (!user) {
      return {};
    }

    // See: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching#reference

    return {
      // FB documentation says hashing external_id optional (but recommended), payload helper says mandatory. We will hash
      external_id: this.hash(user.external_id),
      em: this.hash(user.em),
      fn: this.hash(user.fn),
      ln: this.hash(user.ln),
      ph: this.hash(this.isHashed(user.ph) ? user.ph : user.ph?.replace(/[\s-()+]/g, '')),
      ct: this.hash(this.isHashed(user.ct) ? user.ct : user.ct?.replace(/\s/g, '')),
      st: this.hash(user.st),
      zp: this.hash(this.isHashed(user.zp) ? user.zp : user.zp?.replace(/\s/g, '')),
      ge: this.hash(user.ge),
      db: this.hash(user.db),
      country: this.hash(user.country),
    };
  }

  /** Determines if the input value was already hashed */
  private isHashed(value?: string): boolean {
    if (!value) {
      return false;
    }

    // Regex to test for a 64-character hexadecimal string.
    const sha256Regex = /^[a-f0-9]{64}$/i;

    return sha256Regex.test(value);
  }

  /**
   * Creates a sha-256 hexadecimal string from the given value, as the meta pixel expects. Values will be trimmed and lowercased before hashing.
   * @param value The input value to hash. Will not re-hash if already a 64 character hex string
   * @returns The hexadecimal sha-256 hash of the value, or undefined if no value provided
   */
  private hash(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    if (this.isHashed(value)) {
      return value;
    }

    return sha256(value.trim().toLowerCase());
  }

  private logEventToConsole(type: MetaPixelTrackType, event: string, params?: Record<string, unknown>, options?: MetaEventOptions): void {
    console.group(`[MetaPixel] ${type} event`);
    console.log(`Event: "${event}"`);
    console.log(`Params: `, JSON.stringify(params ?? {}, null, 2));
    console.log(`Options: `, JSON.stringify(options ?? {}, null, 2));
    console.groupEnd();
  }
}
